// Auswertungen der Mitmach-Module in der Suite: Titel bewerten und Hörer-Charts.
//
// Beides hängt an einem Kanal. Hat das Radio nur einen, wird er einfach genommen;
// sonst steht oben eine Auswahl.
//
// Dazu kommen hier die kleinen Teile, die alle Mitmach-Seiten (Mitmachen,
// Gewinnspiel, Frage der Woche, Hörertelefon, Chat, Posteingang …) gemeinsam
// haben: Symbole, Rückmeldungen, Kanalwahl, Ergebnisbalken, Schalter und der
// Hinweis „Modul ist aus“. So sehen diese Seiten untereinander gleich aus.

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, type ChartEntry, type RatedTitle } from "../api";
import type { Channel } from "../types";
import { Badge, Callout, EmptyState, Page, PageHeader } from "./ui";
import { errorText } from "./streaming-ui";

// ---------------------------------------------------------------- Symbole

/**
 * Schlanke Strich-Symbole (24er-Raster, Linienstärke 1,75). Eine eigene
 * Bibliothek lohnt für die Handvoll nicht – und so kommt nichts von außen.
 */
const SYMBOLE: Record<string, ReactNode> = {
  inbox: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>,
  mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 6-10 7L2 6" /></>,
  mic: <><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><path d="M12 19v3" /></>,
  music: <><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  alert: <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></>,
  phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />,
  chat: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  gift: <><path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></>,
  poll: <path d="M18 20V10M12 20V4M6 20v-6" />,
  help: <><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" /></>,
  star: <path d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />,
  trend: <><path d="m23 6-9.5 9.5-5-5L1 18" /><path d="M17 6h6v6" /></>,
  heart: <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />,
  bell: <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>,
  sparkles: <><path d="m12 3 1.9 5.8L20 10l-6.1 1.2L12 17l-1.9-5.8L4 10l6.1-1.2z" /><path d="M19 17v4M17 19h4" /></>,
  send: <><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></>,
  check: <path d="M20 6 9 17l-5-5" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  trash: <><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
  ban: <><circle cx="12" cy="12" r="10" /><path d="m4.9 4.9 14.2 14.2" /></>,
  eyeOff: <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><path d="m1 1 22 22" /></>,
  play: <path d="M6 3l14 9-14 9z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  back: <path d="M19 12H5M12 19l-7-7 7-7" />,
  up: <path d="m18 15-6-6-6 6" />,
  down: <path d="m6 9 6 6 6-6" />,
  copy: <><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>,
  monitor: <><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></>,
  smartphone: <><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></>,
  external: <><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6M10 14 21 3" /></>,
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  key: <><circle cx="7.5" cy="15.5" r="5.5" /><path d="m21 2-9.6 9.6M15.5 7.5l3 3L22 7l-3-3" /></>,
  sliders: <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />,
  clock: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
  radio: <><circle cx="12" cy="12" r="2" /><path d="M16.24 7.76a6 6 0 0 1 0 8.49M7.76 16.24a6 6 0 0 1 0-8.49M19.07 4.93a10 10 0 0 1 0 14.14M4.93 19.07a10 10 0 0 1 0-14.14" /></>,
  headphones: <><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" /></>,
  list: <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />,
  layout: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></>,
  image: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>,
  palette: <><circle cx="13.5" cy="6.5" r="1" /><circle cx="17.5" cy="10.5" r="1" /><circle cx="8.5" cy="7.5" r="1" /><circle cx="6.5" cy="12.5" r="1" /><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.02-.24-.27-.38-.62-.38-1.02 0-.83.67-1.5 1.5-1.5H16c3.31 0 6-2.69 6-6 0-4.96-4.49-9-10-9z" /></>,
  grip: <><circle cx="9" cy="6" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="18" r="1" /></>,
  party: <><path d="M5.8 11.3 2 22l10.7-3.79" /><path d="M4 3h.01M22 8h.01M15 2h.01M22 20h.01M22 2l-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10" /><path d="m11 13 1.8 5.4a.5.5 0 0 0 .95 0L22 2" /></>,
  link: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>,
  download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m7 10 5 5 5-5M12 15V3" /></>,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
};

export type SymbolName = keyof typeof SYMBOLE;

/** Ein Symbol aus dem Satz oben; Größe über die Klasse (Standard 16 px). */
export function Sym({ name, className = "h-4 w-4" }: { name: string; className?: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
      className={`flex-shrink-0 ${className}`} aria-hidden="true">
      {SYMBOLE[name] ?? SYMBOLE.sparkles}
    </svg>
  );
}

// ---------------------------------------------------------------- Kleinteile der Mitmach-Seiten

/** Fehler und Erfolgsmeldung einer Seite – immer an derselben Stelle, in derselben Form. */
export function Meldungen({ error, notice, onClear }: { error?: string | null; notice?: string | null; onClear?: () => void }): JSX.Element | null {
  if (!error && !notice) return null;
  return (
    <div className="mb-4 space-y-2">
      {error && <Callout tone="danger">{error}</Callout>}
      {notice && (
        <div className="callout callout-success items-center">
          <Sym name="check" className="h-4 w-4" />
          <div className="min-w-0 flex-1">{notice}</div>
          {onClear && (
            <button type="button" onClick={onClear} className="rounded p-0.5 opacity-70 hover:opacity-100" aria-label="Meldung schließen">
              <Sym name="x" className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Kanalwahl für den Seitenkopf. Bei nur einem Kanal steht sein Name da, ohne Auswahl. */
export function KanalWahl({ channels, slug, onChange }: { channels: Channel[]; slug: string; onChange: (slug: string) => void }): JSX.Element | null {
  if (channels.length === 0) return null;
  if (channels.length === 1) {
    return (
      <span className="inline-flex h-9 items-center gap-2 rounded-lg border border-default bg-card px-3 text-sm text-secondary">
        <Sym name="radio" className="h-4 w-4 text-muted" />
        {channels[0]!.name}
      </span>
    );
  }
  return (
    <label className="relative inline-flex items-center">
      <span className="sr-only">Kanal</span>
      <Sym name="radio" className="pointer-events-none absolute left-2.5 h-4 w-4 text-muted" />
      <select value={slug} onChange={(e) => onChange(e.target.value)} className="input h-9 w-auto min-w-[10rem] pl-8 pr-8">
        {channels.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
      </select>
    </label>
  );
}

/** Wenn das Modul ausgeschaltet ist: freundlich sagen, wo man es einschaltet. */
export function ModulAus({ title, icon, children }: { title: string; icon: string; children: ReactNode }): JSX.Element {
  return (
    <Page>
      <PageHeader eyebrow="Hörer" title={title} />
      <div className="card p-2">
        <EmptyState
          icon={<Sym name={icon} className="h-5 w-5" />}
          title="Dieses Modul ist ausgeschaltet"
          description={children}
          action={<Link to="/module" className="btn btn-primary">Zu den Mitmach-Modulen</Link>}
        />
      </div>
    </Page>
  );
}

/** Stand einer Sache (Frage, Gewinnspiel …) als Etikett: läuft / geplant / beendet. */
export function StandSchild({ stand, children }: { stand: "aktiv" | "geplant" | "beendet" | "entwurf" | "ausgelost"; children: ReactNode }): JSX.Element {
  const ton = { aktiv: "success", geplant: "info", beendet: "neutral", entwurf: "warning", ausgelost: "brand" } as const;
  return <Badge tone={ton[stand]} dot>{children}</Badge>;
}

/**
 * Ergebnisbalken einer Antwort: Beschriftung links, Anteil als Balken, Zahl rechts.
 * „vorn“ hebt die führende bzw. richtige Antwort in der Hausfarbe hervor.
 */
export function ErgebnisBalken({ label, share, value, vorn = false, leer = false }: {
  label: ReactNode;
  share: number;
  value: ReactNode;
  vorn?: boolean;
  /** Noch keine Stimmen möglich (Entwurf): nur die Beschriftung, kein Balken. */
  leer?: boolean;
}): JSX.Element {
  const breite = Math.max(0, Math.min(100, share));
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className={`min-w-0 truncate ${vorn ? "font-semibold text-primary" : "text-secondary"}`}>{label}</span>
        <span className={`flex-shrink-0 tabular-nums ${vorn ? "font-semibold text-primary" : "text-muted"}`}>{value}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-input" style={{ boxShadow: "inset 0 0 0 1px var(--border)" }}>
        {!leer && (
          <div
            className={`h-full rounded-full transition-[width] duration-500 ${vorn ? "bg-brand-primary" : "bg-brand-primary/40"}`}
            style={{ width: `${breite}%` }}
          />
        )}
      </div>
    </div>
  );
}

/** Ein-/Aus-Schalter in Schalteroptik. Die Logik bleibt beim Aufrufer. */
export function Schalter({ checked, onChange, disabled = false, label }: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${checked ? "bg-brand-primary" : "bg-line-strong"}`}
      style={{ minHeight: 0 }}
    >
      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`} />
    </button>
  );
}

/** Kreis mit Anfangsbuchstaben – für Personen ohne Bild. Farbe bleibt je Name gleich. */
export function Initialen({ name, className = "h-9 w-9 text-sm" }: { name: string; className?: string }): JSX.Element {
  const teile = name.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  const text = ((teile[0]?.[0] ?? "?") + (teile.length > 1 ? teile[teile.length - 1]![0] : "")).toUpperCase();
  // Ein paar ruhige Töne; die Hausfarbe bleibt für Aktionen reserviert.
  const toene = ["bg-sky-500/15 text-sky-700 dark:text-sky-300", "bg-violet-500/15 text-violet-700 dark:text-violet-300",
    "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    "bg-rose-500/15 text-rose-700 dark:text-rose-300", "bg-teal-500/15 text-teal-700 dark:text-teal-300"];
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return (
    <span className={`inline-flex flex-shrink-0 items-center justify-center rounded-full font-semibold ${toene[h % toene.length]} ${className}`} aria-hidden="true">
      {text}
    </span>
  );
}

// ---------------------------------------------------------------- Auswertungen

/** Kanalauswahl, die sich um sich selbst kümmert. */
export function useChannel(): { channels: Channel[]; slug: string; setSlug: (s: string) => void } {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [slug, setSlug] = useState("");
  useEffect(() => {
    api.listChannels()
      .then((list) => {
        setChannels(list);
        setSlug((old) => old || list[0]?.slug || "");
      })
      .catch(() => setChannels([]));
  }, []);
  return { channels, slug, setSlug };
}

export function ChannelPicker({ channels, slug, setSlug }: ReturnType<typeof useChannel>): ReactNode {
  if (channels.length < 2) return null;
  return (
    <label className="flex items-center gap-2 text-sm text-secondary">
      Kanal
      <select value={slug} onChange={(e) => setSlug(e.target.value)} className="input h-8 w-auto py-0">
        {channels.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
      </select>
    </label>
  );
}

const percent = (share: number): string => `${share > 0 ? "+" : ""}${Math.round(share * 100)} %`;

function TitleRows({ rows, empty }: { rows: RatedTitle[]; empty: string }): ReactNode {
  if (rows.length === 0) return <p className="py-3 text-sm text-muted">{empty}</p>;
  return (
    <ul className="divide-y divide-line text-sm">
      {rows.map((r) => (
        <li key={r.item_id} className="flex flex-wrap items-center gap-2 py-2">
          <span className="min-w-0 flex-1 truncate">
            <span className="font-medium text-primary">{r.title}</span>
            {r.artist && <span className="text-muted"> · {r.artist}</span>}
          </span>
          <span className="tabular-nums text-xs text-muted">👍 {r.up} · 👎 {r.down}</span>
          <Badge tone={r.share >= 0 ? "success" : "danger"} className="tabular-nums">{percent(r.share)}</Badge>
        </li>
      ))}
    </ul>
  );
}

/** Was kommt an, was nicht. */
export function RatingReport(): ReactNode {
  const picker = useChannel();
  const { slug } = picker;
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getRatings>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setData(null);
    api.getRatings(slug, days).then(setData).catch((err) => setError(errorText(err)));
  }, [slug, days]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <ChannelPicker {...picker} />
        <label className="flex items-center gap-2 text-sm text-secondary">
          Zeitraum
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="input h-8 w-auto py-0">
            <option value={7}>7 Tage</option>
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
          </select>
        </label>
      </div>
      {error && <Callout tone="danger">{error}</Callout>}
      {!data ? (
        <p className="text-sm text-muted">Lade …</p>
      ) : (
        <>
          <p className="text-sm text-muted">
            Gewertet wird erst ab {data.min_votes} Stimmen je Titel.
            {data.thin > 0 && ` ${data.thin} Titel haben bisher weniger und stehen deshalb nicht in der Liste.`}
            {data.affect_rotation
              ? " Schlecht bewertete Titel kommen in der Automatik seltener – ganz gesperrt wird nie einer."
              : " Die Bewertungen fließen zurzeit nicht in die Titelauswahl ein."}
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="well">
              <h3 className="section-label mb-1">Kommt am besten an</h3>
              <TitleRows rows={data.best} empty="Noch keine Titel mit genug Stimmen." />
            </div>
            <div className="well">
              <h3 className="section-label mb-1">Kommt am schlechtesten an</h3>
              <TitleRows rows={data.worst} empty="Noch keine Titel mit genug Stimmen." />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function move(entry: ChartEntry): ReactNode {
  if (entry.move === null) return <Badge tone="brand">neu</Badge>;
  if (entry.move === 0) return <span className="text-muted">=</span>;
  return entry.move > 0
    ? <span className="font-medium text-emerald-600 dark:text-emerald-400">▲ {entry.move}</span>
    : <span className="font-medium text-red-600 dark:text-red-400">▼ {-entry.move}</span>;
}

/** Die Top-Liste mit Knopf zum Neuberechnen. */
export function ChartReport(): ReactNode {
  const picker = useChannel();
  const { slug } = picker;
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getCharts>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!slug) return;
    api.getCharts(slug).then(setData).catch((err) => setError(errorText(err)));
  }, [slug]);
  useEffect(load, [load]);

  const recompute = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.recomputeCharts(slug);
      load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <ChannelPicker {...picker} />
        <button disabled={busy || !slug} onClick={() => void recompute()} className="btn btn-primary btn-sm">
          {busy ? "Rechnet …" : "Jetzt neu berechnen"}
        </button>
        {data?.playlist && <span className="text-xs text-muted">Playlist „Hörer-Charts“ wird mitgepflegt.</span>}
      </div>
      {error && <Callout tone="danger">{error}</Callout>}
      {!data ? (
        <p className="text-sm text-muted">Lade …</p>
      ) : data.entries.length === 0 ? (
        <p className="text-sm text-muted">
          Noch keine Liste. Sie entsteht aus Wünschen und Bewertungen der letzten {data.period_days} Tage –
          einmal täglich von selbst oder gleich mit dem Knopf oben.
        </p>
      ) : (
        <>
          <p className="text-sm text-muted">
            Woche ab {data.week_start}, gerechnet über {data.period_days} Tage. Ein Wunsch zählt zwei Punkte,
            ein Daumen hoch einen, ein Daumen runter einen ab.
          </p>
          <ol className="divide-y divide-line overflow-hidden rounded-lg border border-default text-sm">
            {data.entries.map((e) => (
              <li key={e.position} className="flex flex-wrap items-center gap-3 px-3 py-2">
                <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-xs font-semibold tabular-nums ${e.position <= 3 ? "bg-brand-primary/15 text-brand-primary" : "bg-input text-secondary"}`}>{e.position}</span>
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium text-primary">{e.title}</span>
                  {e.artist && <span className="text-muted"> · {e.artist}</span>}
                </span>
                <span className="tabular-nums text-xs text-muted">{e.requests}× gewünscht · 👍 {e.up} · 👎 {e.down}</span>
                <span className="w-12 text-right text-xs tabular-nums">{move(e)}</span>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
